import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

interface WizardState {
  currentStep: number;
  totalSteps: number;
  completedSteps: Set<number>;
  formData: Record<string, any>;
}

const initialState: WizardState = {
  currentStep: 1,
  totalSteps: 10,
  completedSteps: new Set(),
  formData: {},
};

const wizardSlice = createSlice({
  name: 'wizard',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    updateFormData: (state, action: PayloadAction<{ key: string; value: any }>) => {
      state.formData[action.payload.key] = action.payload.value;
    },
    resetFormData: (state) => {
      state.formData = {};
    },
    goToStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      state.currentStep = Math.min(state.currentStep + 1, state.totalSteps);
    },
    prevStep: (state) => {
      state.currentStep = Math.max(state.currentStep - 1, 1);
    },
    completeStep: (state, action: PayloadAction<number>) => {
      state.completedSteps = new Set([...state.completedSteps, action.payload]);
    }
  },
});

export const { 
  setCurrentStep, 
  updateFormData, 
  resetFormData, 
  goToStep, 
  nextStep, 
  prevStep, 
  completeStep 
} = wizardSlice.actions;

export const useWizardStore = () => {
  const dispatch = useDispatch();
  const state = useSelector((state: { wizard: WizardState }) => state.wizard);

  return {
    ...state,
    setCurrentStep: (step: number) => dispatch(setCurrentStep(step)),
    updateFormData: (key: string, value: any) => dispatch(updateFormData({ key, value })),
    resetFormData: () => dispatch(resetFormData()),
    goToStep: (step: number) => dispatch(goToStep(step)),
    nextStep: () => dispatch(nextStep()),
    prevStep: () => dispatch(prevStep()),
    completeStep: (step: number) => dispatch(completeStep(step))
  };
};

export default wizardSlice.reducer; 